import { Navigate, useLocation } from 'react-router';
import { Spinner, Stack } from '../ui';
import { useAuth } from './AuthProvider';

/**
 * Gates a route behind sign-in.
 *
 * While auth state is still resolving it shows a spinner rather than bouncing to `/login`, so a
 * signed-in user reloading a protected page is not flashed the login screen. An unauthenticated user
 * is redirected to `/login`, carrying the attempted path so login can return them to it. In
 * development mode the auth context is always authenticated, so this is a pass-through and local
 * development is unchanged.
 *
 * @param props - Component props.
 * @param props.children - The protected content.
 * @returns The gated element.
 */
export function RequireAuth({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <Stack align="center" gap="lg">
        <Spinner label="Checking your sign-in" />
      </Stack>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return <>{children}</>;
}
